import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CampaignChatUI from '@/components/campaign/CampaignChatUI';
import ScrollToTop from '@/components/utils/ScrollToTop';
import { supabase } from '@/integrations/supabase/client';
import { Shield, Heart, DollarSign, Star } from 'lucide-react';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  Shield, Heart, DollarSign, Star,
};

interface CampaignConfig {
  id: string;
  campaign_slug: string;
  campaign_name: string;
  brand_name: string | null;
  brand_color: string | null;
  welcome_message: string | null;
  orientation_options: string[] | null;
  ui_config: Record<string, any> | null;
  product_facts: string[] | null;
}

interface PrefillData {
  age?: number;
  gender?: string;
  smoker_status?: string;
  name?: string;
  email?: string;
  phone?: string;
  prefill_source?: string;
  confirm_mode?: boolean;
}

const CampaignPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState<CampaignConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [prefill, setPrefill] = useState<PrefillData | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      if (!slug) { setNotFound(true); setLoading(false); return; }

      const { data, error } = await supabase
        .from('campaign_configs')
        .select('id, campaign_slug, campaign_name, brand_name, brand_color, welcome_message, orientation_options, ui_config, product_facts')
        .eq('campaign_slug', slug)
        .eq('active', true)
        .single();

      if (error || !data) {
        setNotFound(true);
      } else {
        setConfig(data as unknown as CampaignConfig);
      }
      setLoading(false);
    };
    loadConfig();
  }, [slug]);

  // Prefill: resolve from URL token or query params
  useEffect(() => {
    const resolvePrefill = async () => {
      const token = searchParams.get('lead_token') || searchParams.get('prefill_token');
      
      if (token) {
        // Lookup lead by session_id = token (set during intake)
        const { data: lead } = await supabase
          .from('campaign_leads')
          .select('lead_name, lead_email, lead_phone, age, gender, smoker_status, prefill_source')
          .eq('session_id', token)
          .single();

        if (lead) {
          setPrefill({
            name: lead.lead_name || undefined,
            email: lead.lead_email || undefined,
            phone: lead.lead_phone || undefined,
            age: lead.age || undefined,
            gender: lead.gender || undefined,
            smoker_status: lead.smoker_status || undefined,
            prefill_source: 'token',
            confirm_mode: true,
          });
          return;
        }
      }

      // Fallback: check URL params directly
      const age = searchParams.get('age');
      const gender = searchParams.get('gender');
      const smoker = searchParams.get('smoker_status');
      if (age || gender || smoker) {
        setPrefill({
          age: age ? parseInt(age, 10) : undefined,
          gender: gender || undefined,
          smoker_status: smoker || undefined,
          prefill_source: 'query_params',
          confirm_mode: false,
        });
      }
    };
    resolvePrefill();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner />
      </div>
    );
  }

  if (notFound || !config) {
    return <Navigate to="/404" replace />;
  }

  const ui = config.ui_config || {};
  const pageHeadline = ui.page_headline || config.campaign_name;
  const pageSubheadline = ui.page_subheadline || '';
  const botName = ui.bot_name || `${config.campaign_name} Assistant`;
  const tenantDisplayName = ui.tenant_display_name || config.brand_name || '';
  const disclaimerText = ui.disclaimer_text || 'General information only — not personalised financial advice.';
  const infoCards: Array<{ icon: string; title: string; description: string }> = ui.info_cards || [];
  const orientationOptions: string[] = config.orientation_options || [];

  return (
    <>
      <ScrollToTop />
      <Helmet>
        <title>{pageHeadline} — Get an Indicative Quote | {tenantDisplayName}</title>
        <meta name="description" content={pageSubheadline} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
        <div className="max-w-6xl mx-auto px-4 py-4 md:py-12">
          {/* Mobile-only compact header (above the chat) */}
          <div className="md:hidden mb-3">
            {tenantDisplayName && (
              <p className="text-xs font-medium text-brand-teal uppercase tracking-wider mb-1">{tenantDisplayName}</p>
            )}
            <h1 className="text-xl font-bold text-foreground leading-tight">
              {pageHeadline}
            </h1>
            {pageSubheadline && (
              <p className="text-muted-foreground mt-1 text-sm leading-snug line-clamp-2">
                {pageSubheadline}
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-5 gap-8 items-start">
            {/* Chat panel — first on mobile, right on desktop */}
            <div className="order-1 md:order-2 md:col-span-3 h-[calc(100vh-9rem)] min-h-[480px] md:h-[700px]">
              <CampaignChatUI
                campaignSlug={config.campaign_slug}
                welcomeMessage={config.welcome_message || undefined}
                brandColor={config.brand_color || undefined}
                botName={botName}
                tenantDisplayName={tenantDisplayName}
                sourceChannel="web_landing"
                orientationOptions={orientationOptions}
                disclaimerText={disclaimerText}
                prefill={prefill || undefined}
              />
            </div>

            {/* Info panel — hidden on mobile (compact header above replaces it), shown on desktop */}
            <div className="order-2 md:order-1 md:col-span-2 space-y-6 hidden md:block">
              <div>
                {tenantDisplayName && (
                  <p className="text-sm font-medium text-brand-teal uppercase tracking-wider mb-2">{tenantDisplayName}</p>
                )}
                <h1 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">
                  {pageHeadline}
                </h1>
                {pageSubheadline && (
                  <p className="text-muted-foreground mt-3 text-base leading-relaxed">
                    {pageSubheadline}
                  </p>
                )}
              </div>

              {infoCards.length > 0 && (
                <div className="space-y-4">
                  {infoCards.map((card, idx) => {
                    const IconComponent = ICON_MAP[card.icon] || Shield;
                    return (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-brand-teal/10 flex items-center justify-center shrink-0">
                          <IconComponent className="h-5 w-5 text-brand-teal" />
                        </div>
                        <div>
                          <h3 className="font-medium text-foreground text-sm">{card.title}</h3>
                          <p className="text-xs text-muted-foreground">{card.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {disclaimerText}
                </p>
              </div>
            </div>

            {/* Mobile-only collapsible "Why a strategy session?" info — below chat */}
            {infoCards.length > 0 && (
              <details className="order-3 md:hidden bg-card border border-border rounded-lg p-4 mt-2">
                <summary className="text-sm font-medium text-foreground cursor-pointer select-none">
                  Why a strategy session with {tenantDisplayName || 'us'}?
                </summary>
                <div className="space-y-3 mt-4">
                  {infoCards.map((card, idx) => {
                    const IconComponent = ICON_MAP[card.icon] || Shield;
                    return (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-brand-teal/10 flex items-center justify-center shrink-0">
                          <IconComponent className="h-4 w-4 text-brand-teal" />
                        </div>
                        <div>
                          <h3 className="font-medium text-foreground text-sm">{card.title}</h3>
                          <p className="text-xs text-muted-foreground">{card.description}</p>
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-[11px] text-muted-foreground leading-relaxed pt-2 border-t border-border/50">
                    {disclaimerText}
                  </p>
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CampaignPage;
